import { useCallback } from 'react'
import { useAtomValue } from 'jotai'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { stagedActionEventProperties } from '@renderer/overlay/Sidepanel/hooks/staged-actions/track-staged-action'
import { getQuestionChatTarget, isAskUserQuestionStagedAction } from '@renderer/chat/integrations/ask-user-question'
import { isAnswerableStagedAction } from '@renderer/chat/integrations/answerable'
import { isReportToUserStagedAction } from '@renderer/chat/integrations/report-to-user'
import { useConnectionError } from '../../components/action-connection-error'
import { ActionStatusRing } from '../../components/action-status-ring'
import { HOVER_CARD_TRIGGER } from '../../components/cell-preview-card'
import { COMPLETED_ACTION_DURATION_MS } from '../staged-action-status'
import { useViewActions, type ViewOpenTarget } from '../../view-atoms'
import { useStagedActionCallbacks, useStagedActionStatus } from '../use-staged-action-view'
import { archivedStartByIdAtom } from '../staged-action-local-state'
import { useIsStagedActionEditPending } from '../../atoms'
import { useIsActionHeld } from '../../views/action-view/use-action-lock'
import { isStagedActionUserEdited } from './utils'
import { useConnectionPrerequisite } from './use-connection-prerequisite'
import type { StagedAction, StagedActionView } from './types'

interface UseActionRowModelOptions {
  /** Back-label for the view this row opens. */
  sourceLabel?: string
  /** Amplitude `source` for the events this row emits ('cell', 'meeting-follow-ups', …). */
  eventSource: string
}

/**
 * Everything a staged-action row needs that isn't its own chrome: the
 * prerequisite-resolved view, the derived status flags, and the handlers behind
 * open / invoke / undo / clear.
 *
 * Extracted from `ViewActionCell` so a second surface can host the same row
 * behaviour — and the same hover card — without re-deriving a seven-state
 * machine (staged / invoking / executed / error / undo-error / archived /
 * blocked-on-connection) that would then drift from the Outbox's.
 */
export function useActionRowModel(
  action: StagedAction,
  { sourceLabel = 'Home', eventSource }: UseActionRowModelOptions,
) {
  const {
    invokeStagedAction,
    archiveStagedActionWithUndo,
    undoArchiveStagedAction,
    undoStagedAction,
    markActionExpired,
    markActionAsRead,
  } = useStagedActionCallbacks()
  const {
    status,
    errorMessage,
    isUnread: unread,
    isInvoking: isInvokingThis,
    isUndoing: isUndoingThis,
  } = useStagedActionStatus(action)
  const { openView } = useViewActions()
  const connectionError = useConnectionError(action)
  const {
    unsatisfiedPrerequisite,
    displayView: view,
    ctaLabel,
    warning: prerequisiteWarning,
    connectPrerequisite,
  } = useConnectionPrerequisite(action, () => invokeStagedAction(action.id).catch(console.error))

  // Background AskUserQuestion / AskUserFeedback: answering IS the action's
  // execution, so the primary must open the drawer (where the interactive card
  // lives) rather than go through the invoke machinery — neither synthetic
  // toolset has a hydrator, so invoking one can only fail.
  const isAnswerable = isAnswerableStagedAction(action)
  // Narrower: only a question has a chat to open and question chrome to show.
  const isQuestion = isAskUserQuestionStagedAction(action)

  // report_to_user delivers a message rather than proposing work: there is
  // nothing to execute, so every surface drops the invoke CTA (copy is the only
  // action) and badges the row with the originating skill's icon.
  const isReport = isReportToUserStagedAction(action)
  // A locked preview has nothing to send until it's unblurred, which happens in its view.
  const isLocked = useIsActionHeld(action)

  const isPending = status === 'pending'
  // A draft mid-rewrite is busy in exactly the sense this flag means: the row
  // still shows the pre-edit copy, so its CTA would run the very version the
  // user just asked to change. Folded in here rather than at `isRunning` so the
  // Run/Retry button swaps for the disabled spinner (and ⌘↵ stops firing),
  // instead of only picking up the status ring.
  const isEditPending = useIsStagedActionEditPending(action.id)
  const isInvoking = status === 'invoking' || isEditPending
  const isArchived = status === 'archived'
  const actionHasError = status === 'error'
  const hasUndoError = status === 'undo-error'
  const hasOutput = !!action.lastInvokedAt
  const isExecuted = isPending || (status === 'completed' && hasOutput)
  // `hasOutput` is `lastInvokedAt`, which is set a round trip before there's
  // any result to reverse — so `isExecuted` is what gates undo, not output
  // alone. An undo-error keeps Undo offered: its retry is the same control.
  const canUndo = isExecuted && !!view?.supportsUndo && (hasOutput || hasUndoError)
  // Set only while a just-archived row lingers for its undo window (the sweep
  // clearing the marker is what removes the row from the visible list). Lives
  // here beside `handleUndoArchive` so the gate and its verb can't split.
  const archivedStart = useAtomValue(archivedStartByIdAtom(action.id))
  const archiveWindowStart = isArchived && archivedStart != null ? archivedStart : null
  const inArchiveWindow = archiveWindowStart != null
  const isStaged = status === 'staged'
  // In flight, from either window: `isInvokingThis` is only this window's
  // marker, so a peer's invoke has to be read off the derived status too.
  const isRunning = isInvoking || isInvokingThis
  // An unsatisfied prerequisite outranks the view's own warning: it names the
  // one thing standing between the row and running at all. Only shown while
  // still staged — the source row carries attribution once the warning's moment
  // has passed.
  const warning = prerequisiteWarning ?? (isStaged ? view?.invokeWarning : undefined)

  const openRowView = useCallback(
    ({
      focusContent = false,
      trigger,
      pointerEvent,
      popout,
    }: ViewOpenTarget & { focusContent?: boolean; trigger?: string }) => {
      if (unread) {
        markActionAsRead(action.id, action)
      }
      const options = { sourceLabel, pointerEvent, popout }
      // A background AskUserQuestion lives inside its originating chat — open
      // that conversation (scrolled to the question) so the user can answer it
      // inline with full context. Fall back to the question drawer when there's
      // no chat to open (e.g. a non-chat background run).
      const chatTarget = isQuestion ? getQuestionChatTarget(action) : null
      if (chatTarget) {
        openView('chat', chatTarget, options)
      } else {
        openView(
          'action',
          {
            actionId: action.id,
            title: isAnswerable ? (isQuestion ? 'Question' : 'Feedback') : (view?.title ?? action.title),
            kind: isAnswerable ? (isQuestion ? ('question' as const) : ('feedback' as const)) : undefined,
            focusContent: focusContent || undefined,
          },
          options,
        )
      }
      AmplitudeRenderer.trackEvent('Staged Action', {
        action: 'view',
        ...stagedActionEventProperties(action),
        source: eventSource,
        ...(trigger && { trigger }),
        ...(popout && { popout: true }),
      })
    },
    [action, eventSource, isAnswerable, isQuestion, markActionAsRead, openView, sourceLabel, unread, view?.title],
  )

  /**
   * @param focusContent land with the caret at the end of the body editor (hover card's edit affordance).
   * @param trigger which hover surface initiated this (HOVER_CLUSTER_TRIGGER / HOVER_CARD_TRIGGER); omitted for the row itself.
   */
  const openActionView = useCallback(
    (focusContent = false, trigger?: string, pointerEvent?: ViewOpenTarget['pointerEvent']) =>
      openRowView({ focusContent, trigger, pointerEvent }),
    [openRowView],
  )

  /** Same destination, in its own window — for controls that are only ever the popout. */
  const openActionViewInWindow = useCallback(
    (trigger?: string) => openRowView({ trigger, popout: true }),
    [openRowView],
  )

  const handleInvoke = useCallback(
    (trigger?: string) => {
      if (unsatisfiedPrerequisite) {
        connectPrerequisite()
        return
      }
      if (isAnswerable) {
        openActionView(false, trigger)
        return
      }
      // A held-back action has nothing to send yet; its view says why and offers the way on.
      if (isLocked) {
        openActionView(false, trigger)
        return
      }
      if (view?.invokeLink) {
        window.open(view.invokeLink, '_blank')
      } else {
        invokeStagedAction(action.id).catch(console.error)
      }
      AmplitudeRenderer.trackEvent('Staged Action', {
        action: 'invoke',
        ...stagedActionEventProperties(action),
        status,
        source: eventSource,
        wasEdited: isStagedActionUserEdited(action),
        ...(trigger && { trigger }),
      })
    },
    [
      action,
      connectPrerequisite,
      eventSource,
      invokeStagedAction,
      isAnswerable,
      isLocked,
      openActionView,
      status,
      unsatisfiedPrerequisite,
      view?.invokeLink,
    ],
  )

  const handleUndo = useCallback(
    (trigger?: string) => {
      undoStagedAction(action.id).catch(console.error)
      AmplitudeRenderer.trackEvent('Staged Action', {
        action: 'undo',
        ...stagedActionEventProperties(action),
        status,
        source: eventSource,
        ...(trigger && { trigger }),
      })
    },
    [action, eventSource, status, undoStagedAction],
  )

  const handleClear = useCallback(
    (trigger?: string) => {
      AmplitudeRenderer.trackEvent('Staged Action', {
        action: 'clear',
        ...stagedActionEventProperties(action),
        status,
        source: eventSource,
        ...(trigger && { trigger }),
      })
      if (status === 'staged') {
        // Lingering archive, not the bulk path: the row stays for the undo
        // window with a countdown instead of vanishing on click.
        archiveStagedActionWithUndo(action.id).catch(console.error)
      } else {
        markActionExpired(action.id)
      }
    },
    [action, archiveStagedActionWithUndo, eventSource, markActionExpired, status],
  )

  const handleUndoArchive = useCallback(
    (trigger?: string) => {
      undoArchiveStagedAction(action.id).catch(console.error)
      AmplitudeRenderer.trackEvent('Staged Action', {
        action: 'undo archive',
        ...stagedActionEventProperties(action),
        status,
        source: eventSource,
        ...(trigger && { trigger }),
      })
    },
    [action, eventSource, status, undoArchiveStagedAction],
  )

  return {
    /** Undefined only while the action has no view at all — callers render nothing. */
    view: view as StagedActionView | undefined,
    isQuestion,
    isAnswerable,
    isReport,
    isLocked,
    status,
    unread,
    isStaged,
    isArchived,
    isInvoking,
    isInvokingThis,
    isRunning,
    isExecuted,
    isUndoingThis,
    actionHasError,
    hasUndoError,
    hasOutput,
    canUndo,
    /** When the row's archive-undo countdown started, or null outside the window. */
    archiveWindowStart,
    inArchiveWindow,
    errorMessage,
    connectionError,
    unsatisfiedPrerequisite,
    ctaLabel,
    connectPrerequisite,
    warning,
    openActionView,
    openActionViewInWindow,
    handleInvoke,
    handleUndo,
    handleUndoArchive,
    handleClear,
  }
}

export type ActionRowModel = ReturnType<typeof useActionRowModel>

/**
 * The status ring around a row's integration tile, spanning both halves of
 * executing: a closed grey outline while the action runs, then `tone` and
 * emptying once it lands. Being on screen for the invoke is what stops the
 * countdown appearing part-spent: it's watched from full instead of arriving
 * mid-sweep. The success badge is left to say only that the action landed.
 *
 * An archive counting down gets the neutral tone: nothing executed, the row is
 * only counting down to leaving the list, and green would overclaim.
 *
 * `expiryStartTime` drives the executed countdown; hosts whose executed rows
 * don't expire (the meeting record keeps its rows) simply omit it. No start
 * time also covers the reload that lands on `completed` with the client-side
 * marker gone — nothing left to count down, so the badge stands alone.
 */
export function actionStatusRing(model: ActionRowModel, expiryStartTime?: string | null) {
  if (model.isRunning) return <ActionStatusRing />
  if (model.archiveWindowStart != null) {
    return (
      <ActionStatusRing
        tone="neutral"
        // The raw epoch, not a `new Date` — the ring reseeks its sweep when the
        // startTime identity changes, and a fresh Date every render would
        // restart the countdown on every render.
        expiry={{ duration: COMPLETED_ACTION_DURATION_MS, startTime: model.archiveWindowStart }}
      />
    )
  }
  if (model.isExecuted && expiryStartTime) {
    return <ActionStatusRing expiry={{ duration: COMPLETED_ACTION_DURATION_MS, startTime: expiryStartTime }} />
  }
  return undefined
}

/**
 * The hover card's props, assembled from the row model so a host only has to
 * spread them: `<ActionPreview {...actionPreviewProps(model, action)} />`.
 */
export function actionPreviewProps(
  model: ActionRowModel,
  action: StagedAction,
  onEdit: () => void,
  onOpen: () => void,
) {
  const view = model.view
  if (!view) return null
  return {
    action,
    view,
    isAnswerable: model.isAnswerable,
    isReport: model.isReport,
    isArchived: model.isArchived,
    isInvoking: model.isRunning,
    isExecuted: model.isExecuted,
    hasError: model.actionHasError,
    connectionError: model.connectionError,
    errorMessage: model.errorMessage,
    unsatisfiedPrerequisite: !!model.unsatisfiedPrerequisite,
    ctaLabel: model.ctaLabel,
    onConnectPrerequisite: model.connectPrerequisite,
    onEdit,
    // Dismissing a report from the card is archiving it; gone once archived —
    // there is nothing left to clear.
    onDelete: model.isArchived ? undefined : () => model.handleClear(HOVER_CARD_TRIGGER),
    onArchive: model.isArchived ? undefined : () => model.handleClear(HOVER_CARD_TRIGGER),
    onOpen,
  }
}
